import { useRouter as useNextRouter } from "next/router";
import type React from "react";
import { useLink } from "solito/link";
import { useRouter } from "solito/router";
import type { IconProps } from "@tamagui/helpers-icon";
import { Home as HomeIcon, MessageSquarePlus as MessageSquarePlusIcon, Settings as SettingsIcon, ShieldCheck as ShieldCheckIcon, WalletCards as WalletCardsIcon } from "@tamagui/lucide-icons";
import { GrantedTextLogo, Paragraph, TamaguiButton, View, XStack, YStack } from "@medbillai/ui";
import { ChatIconLogo } from "../../../components/local-images";
import { usePushGetHelp, WEB_SIDEBAR_ASK_GRANTED_SOURCE } from "../../../lib/usePushGetHelp";
const NAV_ITEMS = [{
  icon: HomeIcon,
  label: "Home",
  route: "/",
  matchUrls: ["/"]
}, {
  icon: MessageSquarePlusIcon,
  label: "Cases",
  route: "/cases",
  matchUrls: ["/cases", "/case"]
}, {
  icon: WalletCardsIcon,
  label: "Bills",
  route: "/bills",
  matchUrls: ["/bills", "/bill", "/bill-dispute"]
}, {
  icon: ShieldCheckIcon,
  label: "Accounts",
  route: "/accounts",
  matchUrls: ["/accounts"]
}, {
  icon: SettingsIcon,
  label: "Settings",
  route: "/settings",
  matchUrls: ["/settings", "/hipaa-release", "/people"]
}] as const;
const tabPathMatch = (pathName: string): string => {
  const segments = pathName.split("/");
  const firstSection = `/${segments[1] || ""}`;
  const matchedItem = NAV_ITEMS.find(item => (item.matchUrls as readonly string[]).includes(firstSection));
  return matchedItem?.route ?? "/";
};
type NavItemProps = {
  icon: React.ComponentType<IconProps>;
  label: string;
  route: string;
  isActive: boolean;
};
const NavItem = ({
  icon: Icon,
  label,
  route,
  isActive
}: NavItemProps) => {
  const linkProps = useLink({
    href: route
  });

  // Base colors
  const iconColor = isActive ? "$navy12" : "$textTertiary";
  const textColor = isActive ? "$navy12" : "$textSecondary";
  return <TamaguiButton {...linkProps} unstyled cursor="pointer" paddingHorizontal="$3" paddingVertical="$2" borderRadius="$2" backgroundColor={isActive ? "$backgroundTertiary" : "$cream3"} animation="quick" hoverStyle={{
    backgroundColor: isActive ? "$backgroundTertiary" : "$cream3"
  }} pressStyle={{
    opacity: 0.8
  }} width="100%" group data-sentry-element="TamaguiButton" data-sentry-component="NavItem" data-sentry-source-file="WebNavigation.web.tsx">
      <XStack gap="$2" alignItems="center" width="100%" data-sentry-element="XStack" data-sentry-source-file="WebNavigation.web.tsx">
        <Icon size={16} color={iconColor} strokeWidth={2} $group-hover={{
        color: "$navy12"
      }} data-sentry-element="Icon" data-sentry-source-file="WebNavigation.web.tsx" />
        <Paragraph
      // Using style prop for active state to force fontWeight - Tamagui prop alone wasn't working
      {...isActive && {
        style: {
          fontWeight: "500"
        }
      }} color={textColor} size="$4" $group-hover={isActive ? {
        color: "$navy12"
      } : {
        color: "$navy12",
        fontWeight: "500"
      }} data-sentry-element="Paragraph" data-sentry-source-file="WebNavigation.web.tsx">
          {label}
        </Paragraph>
      </XStack>
    </TamaguiButton>;
};
export const WebNavigation = () => {
  const nextRouter = useNextRouter();
  const router = useRouter();
  const {
    pushGetHelp
  } = usePushGetHelp();
  const currentPath = nextRouter.pathname;
  const activeRoute = tabPathMatch(currentPath);

  // Using requestAnimationFrame allows the button press animation to render.
  // This is safe here since the root layout is mounted and stable through the
  // callback finish.
  const handleAskGrantedPress = () => {
    requestAnimationFrame(() => {
      pushGetHelp({
        caseType: "OTHER",
        title: "Get started with Granted",
        source: WEB_SIDEBAR_ASK_GRANTED_SOURCE
      });
    });
  };
  return <YStack width="100%" $gtSm={{
    width: 200
  }} backgroundColor="$cream3" borderRightWidth={1} borderRightColor="$backgroundTertiary" paddingHorizontal="$5" paddingVertical="$6" height="100%" gap="$6" $sm={{
    borderRightWidth: 0
  }} data-sentry-element="YStack" data-sentry-component="WebNavigation" data-sentry-source-file="WebNavigation.web.tsx">
      <YStack onPress={() => router.push("/")} cursor="pointer" data-sentry-element="YStack" data-sentry-source-file="WebNavigation.web.tsx">
        <GrantedTextLogo width={80} height={16} data-sentry-element="GrantedTextLogo" data-sentry-source-file="WebNavigation.web.tsx" />
      </YStack>

      <YStack group position="relative" data-sentry-element="YStack" data-sentry-source-file="WebNavigation.web.tsx">
        {/* Fake shadow element that sits below the button */}
        <View position="absolute" top={1} left={0} right={0} height={34} backgroundColor="#ADA8A0" borderRadius={63} animation="100ms easeOut" transformOrigin="center center" $group-press={{
        scale: 0.98
      }} data-sentry-element="View" data-sentry-source-file="WebNavigation.web.tsx" />
        <TamaguiButton unstyled cursor="pointer" backgroundColor="$backgroundSecondary" borderRadius={63} height={34} onPress={handleAskGrantedPress} paddingLeft="$2" paddingRight="$3" paddingVertical="$1" borderWidth={1} borderColor="$navy2"
      // Inner shadow only
      boxShadow="0 2px 1px 0 #EEEEC0 inset" flexDirection="row" alignItems="center" justifyContent="flex-start" animation="100ms easeOut" transformOrigin="center center" $group-hover={{
        borderColor: "$strokePrimary",
        y: -1
      }} pressStyle={{
        y: 2,
        scale: 0.98
      }} data-sentry-element="TamaguiButton" data-sentry-source-file="WebNavigation.web.tsx">
          <ChatIconLogo width={20} height={20} data-sentry-element="ChatIconLogo" data-sentry-source-file="WebNavigation.web.tsx" />
          <Paragraph color="$textSecondary" size="$4" whiteSpace="nowrap" animation="100ms easeOut" marginTop="$0.5" // Visually align
        data-sentry-element="Paragraph" data-sentry-source-file="WebNavigation.web.tsx">
            Ask Granted
          </Paragraph>
        </TamaguiButton>
      </YStack>

      <YStack gap="$1" width="100%" data-sentry-element="YStack" data-sentry-source-file="WebNavigation.web.tsx">
        {NAV_ITEMS.map(item => <NavItem key={item.route} icon={item.icon} label={item.label} route={item.route} isActive={activeRoute === item.route} />)}
      </YStack>
    </YStack>;
};